// src/pages/admin/logs/components/LogDetailCard.js
import React, { useState } from 'react';
import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import { ArrowLeft, FileText, Check, Copy } from 'lucide-react';
import Swal from 'sweetalert2';

const LogDetailCard = ({ activeTab, selectedLog, onBack }) => {
    const [copied, setCopied] = useState(false);

    const handleCopyPayload = () => {
        const textToCopy = selectedLog.message || selectedLog.description || selectedLog.details || '';
        navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCopyFullJson = () => {
        const jsonStr = JSON.stringify(selectedLog, null, 2);
        navigator.clipboard.writeText(jsonStr);
        Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: 'Full JSON log object copied to clipboard.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            background: '#151521',
            color: '#ffffff'
        });
    };

    return (
        <Card className="border-0 rounded-4 overflow-hidden mb-4 activity-logs-card shadow-lg">
            <Card.Header className="bg-dark p-3 border-bottom border-secondary d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-3">
                    <Button 
                        variant="outline-light" 
                        size="sm" 
                        className="d-flex align-items-center gap-2 rounded-3 px-3 py-1"
                        onClick={onBack}
                    >
                        <ArrowLeft size={16} /> Back to Logs Table
                    </Button>
                    <h5 className="m-0 text-white fw-bold d-flex align-items-center gap-2">
                        <FileText className="text-primary" size={20} />
                        {activeTab === 'system' ? 'System Log Inspection' : 'Application Audit Action'}
                    </h5>
                </div>
            </Card.Header>

            <Card.Body className="p-4">
                <Form>
                    <Row className="g-3 mb-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="text-secondary small fw-bold uppercase">
                                    {activeTab === 'system' ? 'Source Component' : 'User / Actor Name'}
                                </Form.Label>
                                <Form.Control 
                                    type="text" 
                                    readOnly 
                                    value={activeTab === 'system' ? (selectedLog.source_component || 'SYSTEM') : (selectedLog.user_name || selectedLog.user_id || 'Unauthenticated')}
                                    className="bg-dark text-info border-secondary fw-semibold"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="text-secondary small fw-bold uppercase">
                                    {activeTab === 'system' ? 'Log Level' : 'Action Type'}
                                </Form.Label>
                                <Form.Control 
                                    type="text" 
                                    readOnly 
                                    value={activeTab === 'system' ? (selectedLog.log_level || 'INFO') : (selectedLog.action_type || 'ACTIVITY')}
                                    className={`bg-dark border-secondary fw-bold ${
                                        (selectedLog.log_level || selectedLog.action_type || '').includes('FAIL') || (selectedLog.log_level || '').includes('ERROR') 
                                        ? 'text-danger' 
                                        : 'text-success'
                                    }`}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="text-secondary small fw-bold uppercase">IP Address</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    readOnly 
                                    value={selectedLog.ip_address || '::1'}
                                    className="bg-dark text-white border-secondary font-monospace"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="text-secondary small fw-bold uppercase">Timestamp</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    readOnly 
                                    value={new Date(selectedLog.created_at || Date.now()).toLocaleString('en-IN')}
                                    className="bg-dark text-white border-secondary"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="g-3 mb-4">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-secondary small fw-bold uppercase">User Role / System Module</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    readOnly 
                                    value={selectedLog.user_role || selectedLog.module_name || 'AUTHENTICATION_GATEWAY'}
                                    className="bg-dark text-white border-secondary"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="text-secondary small fw-bold uppercase">Log Database ID</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    readOnly 
                                    value={`LOG_RECORD_ID #${selectedLog.id || '101'}`}
                                    className="bg-dark text-white border-secondary font-monospace"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="text-secondary small fw-bold uppercase mb-0">
                                Log Message & Payload Details
                            </Form.Label>
                            <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="py-0 px-2"
                                onClick={handleCopyPayload}
                            >
                                {copied ? <Check size={12} className="text-success me-1" /> : <Copy size={12} className="me-1" />}
                                {copied ? 'Copied to Clipboard' : 'Copy Payload'}
                            </Button>
                        </div>
                        <Form.Control 
                            as="textarea" 
                            rows={5} 
                            readOnly 
                            value={selectedLog.message || selectedLog.description || selectedLog.details || 'No additional event details payload recorded.'}
                            style={{
                                fontFamily: 'monospace',
                                backgroundColor: '#090d16',
                                color: (selectedLog.log_level || selectedLog.action_type || '').includes('FAIL') || (selectedLog.log_level || '').includes('ERROR') ? '#F87171' : '#38BDF8',
                                borderColor: '#2D3142'
                            }}
                        />
                    </Form.Group>

                    <div className="d-flex justify-content-between align-items-center border-top border-secondary pt-3">
                        <Button 
                            variant="secondary" 
                            className="px-4"
                            onClick={onBack}
                        >
                            ← Back to Log List
                        </Button>
                        <Button 
                            variant="primary" 
                            className="px-4"
                            onClick={handleCopyFullJson}
                        >
                            Copy Full JSON Object
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default LogDetailCard;
